import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import notAuth from "@/router/middleware/notAuth";
import auth from "@/router/middleware/auth";
import middlewarePipeline from "@/router/MiddlewarePipeline";
import claim from "@/router/middleware/claim";
import { Claim } from "@/common/claims/claimsEnum";
import { useAppStore } from "@/stores/app";
import { headerTitleMetaMap } from "@/common/headerTitleMetaMap";
import SuppliesView from "@/views/orders/supplies/SuppliesView.vue";
import BoxesView from "@/views/orders/boxes/BoxesView.vue";
import CombineProductsView from "@/views/goods/combineProducts/CombineProductsView.vue";
import CombineProductsBySize from "@/components/product/combineProducts/combineProductsBySize/CombineProductsBySize.vue";
import CombineProductsByVariant from "@/components/product/combineProducts/combineProductsByVariant/CombineProductsByVariant.vue";
import RegistryMarkingCodes from "@/components/product/marking/registryMarkingCodes/RegistryMarkingCodes.vue";
import MarkingCodesView from "@/views/goods/marking/MarkingCodesView.vue";
import DocumentsMarkingCodes from "@/components/product/marking/documentsMarkingCodes/DocumentsMarkingCodes.vue";
import ProfileView from "@/views/profile/ProfileView.vue";
import Profile from "@/components/profile/Profile.vue";
import ProfileChangePassword from "@/components/profile/ProfileChangePassword.vue";
import SupportUser from "@/components/settings/support/user/SupportUser.vue";
import SupportUserCreate from "@/components/settings/support/user/SupportUserCreate.vue";
import SupportAdmin from "@/components/settings/support/admin/SupportAdmin.vue";
import SupportSingleChat from "@/components/settings/support/SupportSingleChat.vue";
import LabelTemplateEdit from "@/components/settings/labels/labelTemplateEdit/LabelTemplateEdit.vue";
import LabelTemplateTables from "@/components/settings/labels/labelTemplateTable/LabelTemplateTables.vue";
import { roleGuard } from "@/router/middleware/roleGuard";
import { UserRole } from "@/api/api.dto";

export interface MiddlewareContext {
  to: RouteLocationNormalized;
  from: RouteLocationNormalized;
  next: NavigationGuardNext;
  nextMiddleware?: () => void;
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          resolve({
            el: decodeURIComponent(to.hash),
            top: 0,
            behavior: "smooth",
          });
        });
      });
    }

    return { top: 0 };
  },
  routes: [
    {
      path: "/:pathMatch(.*)*",
      redirect: { name: "start" },
    },
    {
      path: "/enter",
      name: "enter",
      component: () => import("@/views/BaseLayout.vue"),
      meta: {
        middleware: [auth],
        tourName: "mainMenuTour",
      },
      children: [
        {
          path: "/main",
          name: "main",
          component: () => import("@/views/MainView.vue"),
          meta: {
            middleware: [auth, claim],
            tourName: "mainMenuTour",
            // claim: enumClaims["Главная"],
          },
        },
        {
          path: "/profile",
          component: ProfileView,
          redirect: { name: "profile" },
          meta: { titleInfo: headerTitleMetaMap.profile },
          children: [
            {
              path: "",
              name: "profile",
              component: Profile,
              meta: {
                middleware: [auth],
              },
            },
            {
              path: "/profile/changePassword",
              name: "profileChangePassword",
              component: ProfileChangePassword,
              meta: {
                middleware: [auth],
              },
            },
          ],
        },
        {
          path: "/products",
          name: "products",
          component: () => import("@/views/goods/products/ProductsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: Claim.Catalog,
          },
          redirect: { name: "productsList" },
          children: [
            {
              path: "/products/list",
              name: "productsList",
              component: () =>
                import("@/views/goods/products/ProductsListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Catalog,
                titleInfo: headerTitleMetaMap.products,
              },
            },
            {
              path: "/products/create",
              name: "productCreate",
              component: () =>
                import("@/views/goods/products/ProductCreateView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Catalog,
                titleInfo: headerTitleMetaMap.addProduct,
              },
            },
            {
              path: "/products/card",
              name: "product",
              component: () => import("@/views/goods/products/ProductView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Catalog,
                titleInfo: headerTitleMetaMap.editProduct,
              },
            },
          ],
        },
        {
          path: "/categories",
          name: "categories",
          redirect: { name: "categoriesList" },
          meta: {
            middleware: [auth, claim],
            claim: Claim.Categories,
          },
          component: () =>
            import("@/views/customCategories/CategoriesView.vue"),
          children: [
            {
              path: "/categories/list",
              name: "categoriesList",
              component: () =>
                import("@/views/customCategories/CategoriesListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Categories,
                titleInfo: headerTitleMetaMap.categories,
              },
            },
            {
              path: "/categories/:id",
              name: "category",
              component: () =>
                import("@/views/customCategories/CategoryView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Categories,
                titleInfo: headerTitleMetaMap.editCategory,
              },
            },
            {
              path: "/categories/add",
              name: "categoriesAdd",
              component: () =>
                import("@/views/customCategories/CategoriesAddView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Categories,
                titleInfo: headerTitleMetaMap.addCategory,
              },
            },
          ],
        },
        {
          path: "/combine",
          name: "combineProducts",
          redirect: { name: "combineProductsBySize" },
          meta: {
            middleware: [auth, claim],
            claim: Claim.MergeProducts,
          },
          component: CombineProductsView,
          children: [
            {
              path: "/combine/size",
              name: "combineProductsBySize",
              component: CombineProductsBySize,
              meta: {
                middleware: [auth, claim],
                claim: Claim.MergeProducts,
                titleInfo: headerTitleMetaMap.combineProducts,
              },
            },
            {
              path: "/combine/variant",
              name: "combineProductsByVariant",
              component: CombineProductsByVariant,
              meta: {
                middleware: [auth, claim],
                claim: Claim.MergeProducts,
                titleInfo: headerTitleMetaMap.combineProducts,
              },
            },
          ],
        },
        {
          path: "/marking",
          name: "markingCodes",
          redirect: { name: "registryMarkingCodes" },
          meta: {
            middleware: [auth, claim],
            claim: Claim.ProductLabeling,
          },
          component: MarkingCodesView,
          children: [
            {
              path: "/marking/registry",
              name: "registryMarkingCodes",
              component: RegistryMarkingCodes,
              meta: {
                middleware: [auth, claim],
                claim: Claim.ProductLabeling,
                titleInfo: headerTitleMetaMap.registryMarkingCodes,
              },
            },
            {
              path: "/marking/documents",
              name: "documentsMarkingCodes",
              component: DocumentsMarkingCodes,
              meta: {
                middleware: [auth, claim],
                claim: Claim.ProductLabeling,
                titleInfo: headerTitleMetaMap.documentsMarkingCodes,
              },
            },
          ],
        },
        {
          path: "/analytics",
          name: "analytics",
          component: () => import("@/views/AnalyticsView.vue"),
          meta: {
            middleware: [auth, claim],
            // claim: enumClaims["Аналитика"],
          },
        },
        {
          path: "/reviews",
          name: "reviews",
          component: () => import("@/views/ReviewsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: Claim.Feedbacks,
          },
        },
        {
          path: "/price-management",
          name: "priceManagement",
          component: () => import("@/views/PriceManagementView.vue"),
          meta: {
            middleware: [auth, claim],
            // claim: enumClaims["Управление ценами"],
          },
        },
        {
          path: "/warehouses",
          name: "warehouses",
          component: () =>
            import("@/views/warehouseAndRemains/warehouses/WarehousesView.vue"),
          redirect: { name: "warehousesList" },
          meta: {
            middleware: [auth, claim],
            claim: Claim.WarehouseAndRemains,
          },
          children: [
            {
              path: "/warehouses/list",
              name: "warehousesList",
              component: () =>
                import("@/views/warehouseAndRemains/warehouses/WarehouseListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Warehouses,
                titleInfo: headerTitleMetaMap.warehouses,
              },
            },
            {
              path: "/warehouses/add",
              name: "warehouseAdd",
              component: () =>
                import("@/views/warehouseAndRemains/warehouses/WarehouseAddView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Warehouses,
                titleInfo: headerTitleMetaMap.addWarehouse,
              },
            },
            {
              path: "/warehouses/:warehouseId",
              name: "warehouse",
              component: () =>
                import("@/views/warehouseAndRemains/warehouses/WarehouseView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Warehouses,
                titleInfo: headerTitleMetaMap.editWarehouse,
              },
            },
          ],
        },
        {
          path: "/remains",
          name: "remainsParent",
          component: () =>
            import("@/views/warehouseAndRemains/remains/RemainsParentView.vue"),
          redirect: { name: "remainsTable" },
          meta: {
            middleware: [auth, claim],
            claim: Claim.Stocks,
          },
          children: [
            {
              path: "/remains/table",
              name: "remainsTable",
              component: () =>
                import("@/views/warehouseAndRemains/remains/RemainsTableView.vue"),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.remains,
                claim: Claim.Stocks,
              },
            },
          ],
        },
        {
          path: "/redistribution",
          name: "redistribution",
          component: () =>
            import("@/views/warehouseAndRemains/redistribution/RedistributionManagementView.vue"),
          redirect: { name: "manualRedistribution" },
          meta: {
            middleware: [auth, claim],
            claim: Claim.HasAccessToRedistribution,
          },
          children: [
            {
              path: "/redistribution/manual",
              name: "manualRedistribution",
              component: () =>
                import("@/views/warehouseAndRemains/redistribution/ManualRedistributionView.vue"),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.manualRedistribution,
                claim: Claim.HasAccessToRedistribution,
              },
            },
            {
              path: "/redistribution/auto",
              name: "automatedRedistribution",
              component: () =>
                import("@/views/warehouseAndRemains/redistribution/AutomatedRedistributionView.vue"),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.automatedRedistribution,
                claim: Claim.HasAccessToRedistribution,
              },
            },
            {
              path: "/redistribution/tasks",
              name: "redistributionTasks",
              component: () =>
                import("@/views/warehouseAndRemains/redistribution/RedistributionTasksView.vue"),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.redistributionTasks,
                claim: Claim.HasAccessToRedistribution,
              },
            },
          ],
        },
        {
          path: "/orders",
          name: "orders",
          component: () => import("@/views/orders/OrdersView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: Claim.Orders,
          },
          children: [
            {
              path: "/orders/fbs/:status",
              name: "ordersFbs",
              component: () => import("@/views/orders/fbs/FbsView.vue"),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.fbsOrders,
                claim: Claim.OwnWarehouseFbs,
              },
            },
            {
              path: "/orders/boxes",
              component: BoxesView,
              children: [
                {
                  path: "",
                  name: "boxes",
                  component: () =>
                    import("@/components/orders/boxes/Boxes.vue"),
                  meta: {
                    middleware: [auth, claim],
                    titleInfo: headerTitleMetaMap.boxes,
                    claim: Claim.Orders,
                  },
                },
                {
                  path: ":boxId",
                  name: "boxSingle",
                  component: () =>
                    import("@/components/orders/boxes/boxSingle/BoxSingle.vue"),
                  meta: {
                    middleware: [auth, claim],
                    titleInfo: headerTitleMetaMap.boxesSingle,
                    claim: Claim.Orders,
                  },
                },
              ],
            },
            {
              path: "/orders/supplies",
              component: SuppliesView,
              children: [
                {
                  path: "",
                  name: "supplies",
                  component: () =>
                    import("@/components/orders/supplies/suppliesTable/SuppliesTable.vue"),
                  meta: {
                    middleware: [auth, claim],
                    titleInfo: headerTitleMetaMap.supplies,
                    claim: Claim.Supplies,
                  },
                },
                {
                  path: ":supplyId",
                  name: "suppliesSingle",
                  component: () =>
                    import("@/components/orders/supplies/suppliesSingle/SuppliesSingle.vue"),
                  meta: {
                    middleware: [auth, claim],
                    claim: Claim.Supplies,
                    titleInfo: headerTitleMetaMap.suppliesSingle,
                  },
                },
              ],
            },
            {
              path: "/orders/:assemblyTaskId",
              name: "assemblyTask",
              component: () => import("@/views/orders/OrderSingleView.vue"),
              meta: {
                middleware: [auth, claim],
                titleInfo: headerTitleMetaMap.orderSingle,
                claim: Claim.Orders,
              },
            },
          ],
        },
        {
          path: "/settings",
          name: "settings",
          component: () => import("@/views/settings/SettingsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: Claim.Settings,
          },
        },
        {
          path: "/organizations",
          name: "organizations",
          component: () => import("@/views/settings/OrganizationsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: Claim.Organizations,
            titleInfo: headerTitleMetaMap.organizations,
          },
        },
        {
          path: "/integration",
          name: "integration",
          component: () => import("@/views/integration/IntegrationView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: Claim.Integrations,
          },
          redirect: { name: "integrationList" },
          children: [
            {
              path: "/integration/list",
              name: "integrationList",
              component: () =>
                import("@/views/integration/IntegrationListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Integrations,
                titleInfo: headerTitleMetaMap.integrations,
              },
            },
            {
              path: "/integration/:market",
              name: "integrationSettings",
              component: () =>
                import("@/views/integration/IntegrationSettings.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Integrations,
                titleInfo: headerTitleMetaMap.integrations,
              },
            },
          ],
        },
        {
          path: "/staff",
          name: "staff",
          component: () => import("@/views/settings/staff/StaffView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: Claim.Employees,
          },
          redirect: { name: "staffList" },
          children: [
            {
              path: "/staff/list",
              name: "staffList",
              component: () =>
                import("@/views/settings/staff/StaffListView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Employees,
                titleInfo: headerTitleMetaMap.staff,
              },
            },
            {
              path: "/staff/add",
              name: "staffUserAdd",
              component: () =>
                import("@/components/settings/staff/AddUser.vue"), // TODO
              meta: {
                middleware: [auth, claim],
                claim: Claim.Employees,
                titleInfo: headerTitleMetaMap.staffAdd,
              },
            },
            {
              path: "/staff/:id",
              name: "staffUser",
              component: () =>
                import("@/views/settings/staff/StaffUserView.vue"),
              meta: {
                middleware: [auth, claim],
                claim: Claim.Employees,
                titleInfo: headerTitleMetaMap.staffAccess,
              },
            },
          ],
        },
        {
          path: "/labels",
          name: "labels",
          component: () => import("@/views/settings/labels/LabelsView.vue"),
          meta: {
            middleware: [auth, claim],
            claim: Claim.Labels,
            titleInfo: headerTitleMetaMap.labels,
          },
          redirect: { name: "labelsList" },
          children: [
            {
              path: ":labelId",
              name: "labelsEdit",
              component: LabelTemplateEdit,
              meta: {
                titleInfo: headerTitleMetaMap.editLabel,
                middleware: [auth, claim],
                claim: Claim.Labels,
              },
            },
            {
              path: "list",
              name: "labelsList",
              component: LabelTemplateTables,
              meta: {
                titleInfo: headerTitleMetaMap.labels,
                middleware: [auth, claim],
                claim: Claim.Labels,
              },
            },
          ],
        },
        {
          path: "/partnerLogs",
          name: "partnerLogs",
          component: () =>
            import("@/views/settings/partnerLogs/PartnerLogsView.vue"),
          meta: {
            middleware: [auth, claim],
            titleInfo: headerTitleMetaMap.partnerLogs,
            claim: Claim.Logs,
          },
        },
        {
          path: "/support",
          name: "support",
          component: () => import("@/views/settings/support/SupportView.vue"),
          meta: {
            middleware: [auth],
            titleInfo: headerTitleMetaMap.support,
          },
          children: [
            {
              path: "user",
              name: "supportUser",
              component: SupportUser,
              redirect: { name: "supportUserRequestCreate" },
              children: [
                {
                  path: "create",
                  name: "supportUserRequestCreate",
                  component: SupportUserCreate,
                  beforeEnter: [roleGuard([UserRole.Owner, UserRole.Employee])],
                },
                {
                  path: ":supportRequestId",
                  name: "supportUserRequestSingle",
                  component: SupportSingleChat,
                },
              ],
            },
            {
              path: "admin",
              name: "supportAdmin",
              component: SupportAdmin,
              meta: {
                claim: Claim.AdminSupport,
              },
              beforeEnter: [roleGuard([UserRole.Support])],
              children: [
                {
                  path: ":supportRequestId",
                  name: "supportAdminRequestSingle",
                  component: SupportSingleChat,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "/",
      name: "start",
      component: () => import("@/views/auth/enterPage/ui/EnterPage.vue"),
      meta: {
        middleware: [notAuth],
        hideRotateDeviceOverlay: true,
      },
    },
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/auth/LoginView.vue"),
      meta: {
        middleware: [notAuth],
        hideRotateDeviceOverlay: true,
      },
    },
    {
      path: "/registration",
      name: "registration",
      component: () => import("@/views/auth/RegistrationView.vue"),
      meta: {
        middleware: [notAuth],
        hideRotateDeviceOverlay: true,
      },
    },
    {
      path: "/permissionDenied",
      name: "permissionDenied",
      component: () => import("@/views/PermissionDeniedView.vue"),
    },
    {
      path: "/test",
      name: "test",
      component: () => import("@/views/TestView.vue"),
    },
  ],
});

router.beforeEach((to, from, next) => {
  const appStore = useAppStore();

  // Проверка на несохранённые изменения
  if (appStore.hasNotSavedChanges) {
    if (from.name) {
      const confirmLeave = window.confirm(
        appStore.textForConfirm ||
          "У вас есть несохранённые изменения. Вы уверены, что хотите покинуть страницу?",
      );
      if (!confirmLeave) {
        return next(false); // Отмена перехода
      } else {
        appStore.resetNotSavedChanges();
      }
    }
  }

  // Если нет middleware
  if (!to.meta.middleware) {
    return next();
  }

  // Middleware обработка
  const middleware: any = to.meta.middleware;
  const context: MiddlewareContext = {
    to,
    from,
    next,
  };

  return middleware[0]({
    ...context,
    nextMiddleware: middlewarePipeline(context, middleware, 1),
  });
});

export default router;
